import { Fragment, useState } from "react";

import { UseMutationResult } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { IMergeRequest } from "../../data/useAccountDuplicates";
import useContactLeads from "../../data/useContactLeads";
import { AccountType, IAccount } from "../../models/account/IAccount";
import { IDuplicateAccountRow, IDuplicateContactRow, IDuplicateGroup } from "../../models/account/IDuplicates";
import { IContact } from "../../models/Contact";
import { DefaultResponseWithData } from "../../models/system";
import { bankChoices, mergePlan, onLeftOutAccount, rowOutcome } from "../../utils/contactDuplicates";
import useNotification from "../notifications/useNotifications";
import Card from "../shared/Card/Card";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import SharedContactLeadsTable from "../contacts/SharedContactLeadsTable";

// One contact row's leads, fetched only when the row is opened so the report itself - already minutes
// on a big customer - gets no slower.
function ContactRowLeads({ contactId }: { contactId: string }) {
  const { contactLeads } = useContactLeads(contactId, "DuplicateGroupCard");

  if (contactLeads.isLoading) return <LoadingWheel />;

  return <SharedContactLeadsTable leads={contactLeads.data?.data ?? []} variant="table" />;
}

interface DuplicateGroupCardProps {
  group: IDuplicateGroup;
  mergeAccounts: UseMutationResult<DefaultResponseWithData<IAccount>, unknown, IMergeRequest>;
  mergeContacts: UseMutationResult<DefaultResponseWithData<IContact>, unknown, IMergeRequest>;
  // The person is now one contact and one account: take them off the page.
  onMerged?: () => void;
}

const formatDate = (iso: string) => (iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_SHORT) : "");

// One person on the Duplicates page: their contact rows and accounts, what each still carries, and
// what the tidy would do. Duplicates with history are merged here by hand, into whichever row the
// admin chooses to keep (the suggested one by default).
export default function DuplicateGroupCard({ group, mergeAccounts, mergeContacts, onMerged }: DuplicateGroupCardProps) {
  const { t } = useTranslation("accounts");
  const { addNotification } = useNotification();
  const [keptContactId, setKeptContactId] = useState(group.keptContactId);
  const [keptAccountId, setKeptAccountId] = useState(group.keptAccountId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  // Rows the admin unticked: a household or a namesake the report grouped in, not this person.
  const [leftOut, setLeftOut] = useState<string[]>([]);
  const [bankFromId, setBankFromId] = useState<string | null>(null);

  const keepContact = (id: string) => {
    setKeptContactId(id);
    setLeftOut((current) => current.filter((x) => x !== id));
  };
  const keepAccount = (id: string) => {
    setKeptAccountId(id);
    setLeftOut((current) => current.filter((x) => x !== id));
  };
  const toggleLeftOut = (id: string) =>
    setLeftOut((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  // Contact rows whose leads are showing.
  const [leadsOpen, setLeadsOpen] = useState<string[]>([]);
  const toggleLeadsOpen = (id: string) =>
    setLeadsOpen((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const plan = mergePlan(group, keptContactId, keptAccountId, leftOut);
  const sourceAccounts = group.accounts.filter((a) => plan.accounts?.sourceIds.includes(a.id));
  const sourceContacts = group.contacts.filter((c) => plan.contacts?.sourceIds.includes(c.id));
  const keptContact = group.contacts.find((c) => c.id === keptContactId);
  const keptAccount = group.accounts.find((a) => a.id === keptAccountId);
  const accountNumberFor = (accountId: string | null) =>
    group.accounts.find((a) => a.id === accountId)?.accountNumber;

  // Only asked when the accounts being merged hold different bank details: new leads for this person
  // are paid to the set kept, and the others are written into the kept account's notes.
  const choices = plan.accounts ? bankChoices(group, keptAccountId, leftOut) : [];
  const bankFrom = choices.find((c) => c.accountId === bankFromId) ?? choices[0] ?? null;
  const droppedBank = choices.filter((c) => c !== bankFrom);
  const leftOutRows =
    group.accounts.filter((a) => a.id !== keptAccountId && leftOut.includes(a.id)).length +
    group.contacts.filter((c) => c.id !== keptContactId && !plan.contacts?.sourceIds.includes(c.id)).length;

  const typeLabel = (type: AccountType) => (type === AccountType.Manual ? t("Regulars") : t(AccountType[type]));

  const contactHistory = (row: IDuplicateContactRow) =>
    [
      row.leads > 0 && t("Leads: {{count}}", { count: row.leads }),
      row.orders > 0 && t("Orders: {{count}}", { count: row.orders }),
      row.messages > 0 && t("Messages: {{count}}", { count: row.messages }),
      row.emails > 0 && t("Emails: {{count}}", { count: row.emails }),
      row.documents > 0 && t("Documents: {{count}}", { count: row.documents }),
      row.leadHistory > 0 && t("Lead history: {{count}}", { count: row.leadHistory }),
    ].filter(Boolean).join(", ") || t("Nothing");

  const accountHistory = (row: IDuplicateAccountRow) =>
    [
      row.invoices > 0 && t("Invoices: {{count}}", { count: row.invoices }),
      row.tillSales > 0 && t("Till sales: {{count}}", { count: row.tillSales }),
      row.salesQuotes > 0 && t("Quotes: {{count}}", { count: row.salesQuotes }),
      row.parts > 0 && t("Parts: {{count}}", { count: row.parts }),
      row.partCategories > 0 && t("Part categories: {{count}}", { count: row.partCategories }),
      row.partPricing > 0 && t("Part prices: {{count}}", { count: row.partPricing }),
      row.wasteReceipts > 0 && t("Waste receipts: {{count}}", { count: row.wasteReceipts }),
      row.otherContacts > 0 && t("Other contacts: {{count}}", { count: row.otherContacts }),
      row.notes > 0 && t("Notes: {{count}}", { count: row.notes }),
    ].filter(Boolean).join(", ") || t("Nothing");

  // The suggested keeper's verdicts only hold while the suggested rows are kept.
  const usingSuggestion = keptContactId === group.keptContactId && keptAccountId === group.keptAccountId;

  const status = (row: { tidyable: boolean; reason: string | null; id: string }, keptId: string | null, excluded = false) => {
    if (row.id !== keptId && (leftOut.includes(row.id) || excluded)) return <span className="text-gray-500">{t("Left out")}</span>;
    const outcome = rowOutcome(row, keptId, usingSuggestion);
    if (outcome.kind === "kept") return <span className="font-medium text-green-700">{t("Kept")}</span>;
    if (outcome.kind === "merged") return <span className="text-gray-600">{t("Merged in")}</span>;
    if (outcome.kind === "tidied") return <span className="text-gray-600">{t("Tidied away (empty)")}</span>;
    return <span className="text-amber-700">{outcome.reason}</span>;
  };

  // Accounts first: contacts on two different accounts are refused until those accounts are one.
  const merge = async () => {
    setIsMerging(true);
    try {
      const plan = mergePlan(group, keptContactId, keptAccountId, leftOut, bankFrom?.accountId ?? null);

      if (plan.accounts) {
        const res = await mergeAccounts.mutateAsync(plan.accounts);
        addNotification({ variant: "success", primaryText: res.message ?? t("Accounts merged") });
      }

      if (plan.contacts) {
        const res = await mergeContacts.mutateAsync(plan.contacts);
        addNotification({ variant: "success", primaryText: res.message ?? t("Contacts merged") });
      }

      setConfirmOpen(false);
      // Rows left out are still duplicates in the report's eyes: keep the card so they stay visible.
      if (leftOutRows === 0) onMerged?.();
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: t("The merge did not finish"),
        secondaryText: error?.message,
      });
    } finally {
      setIsMerging(false);
    }
  };

  const badges = (
    <div className="flex flex-wrap items-center justify-end gap-2 text-xs font-medium">
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
        {t("Contacts: {{count}}", { count: group.contacts.length })}
      </span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
        {t("Accounts: {{count}}", { count: group.accounts.length })}
      </span>
      {group.relinksKeptContact && (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">{t("Re-links contact")}</span>
      )}
      {group.needsReview ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">{t("Needs review")}</span>
      ) : (
        <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">{t("Tidy handles it")}</span>
      )}
    </div>
  );

  return (
    <>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        isLoading={isMerging}
        title={t("Merge into the kept rows?")}
        message={[
          keptAccount && sourceAccounts.length > 0
            ? t("{{count}} account(s) will be merged into account #{{number}}.", {
                count: sourceAccounts.length,
                number: keptAccount.accountNumber,
              })
            : null,
          ...(keptAccount
            ? sourceAccounts
                .filter((a) => a.otherContacts > 0)
                .map((a) =>
                  t("Account #{{from}} also has {{count}} other contact(s) - they move to account #{{to}}.", {
                    from: a.accountNumber,
                    count: a.otherContacts,
                    to: keptAccount.accountNumber,
                  })
                )
            : []),
          sourceContacts.length > 0
            ? t("{{count}} contact(s) will be merged into {{name}}.", {
                count: sourceContacts.length,
                name: keptContact?.fullname ?? "",
              })
            : null,
          bankFrom && droppedBank.length > 0
            ? t("Bank details kept: {{kept}}. Not kept, but written into the account's notes: {{dropped}}.", {
                kept: bankFrom.bankDetails,
                dropped: droppedBank.map((c) => c.bankDetails).join("; "),
              })
            : null,
          leftOutRows > 0 ? t("{{count}} row(s) you unticked are left as they are.", { count: leftOutRows }) : null,
          t("Their invoices, leads, orders, messages, documents and addresses move across, and the merged rows are removed. A merged contact can be brought back from the kept contact's Merge history."),
        ]
          .filter(Boolean)
          .join("\n\n")}
        confirmButtonText={t("Merge")}
        onConfirm={merge}
      />

      <Card
        title={group.name || t("(no name)")}
        subtitle={group.matchedOn.join(" · ")}
        headerRight={badges}
        collapsible
        defaultOpen={false}
        bodyClassName="p-4 space-y-6"
      >
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-700">{t("Contacts")}</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">{t("Keep")}</th>
                  <th className="px-3 py-2">{t("Merge")}</th>
                  <th className="px-3 py-2">{t("Name")}</th>
                  <th className="px-3 py-2">{t("Email")}</th>
                  <th className="px-3 py-2">{t("Phone")}</th>
                  <th className="px-3 py-2">{t("eBay Username")}</th>
                  <th className="px-3 py-2">{t("Account")}</th>
                  <th className="px-3 py-2">{t("Created")}</th>
                  <th className="px-3 py-2">{t("History")}</th>
                  <th className="px-3 py-2">{t("What happens")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {group.contacts.map((row) => (
                  <Fragment key={row.id}>
                  <tr className={row.id === keptContactId ? "bg-green-50" : ""}>
                    <td className="px-3 py-2">
                      <input
                        type="radio"
                        name={`kept-contact-${group.keptContactId}`}
                        checked={row.id === keptContactId}
                        onChange={() => keepContact(row.id)}
                        aria-label={t("Keep this contact")}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {row.id !== keptContactId && (
                        <input
                          type="checkbox"
                          checked={!leftOut.includes(row.id) && !onLeftOutAccount(row, keptAccountId, leftOut)}
                          disabled={onLeftOutAccount(row, keptAccountId, leftOut)}
                          onChange={() => toggleLeftOut(row.id)}
                          title={
                            onLeftOutAccount(row, keptAccountId, leftOut)
                              ? t("Stays with its account, which you left out")
                              : undefined
                          }
                          aria-label={t("Merge this contact")}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/contacts/${row.id}`} className="text-primary-600 hover:underline">
                        {row.fullname || t("(no name)")}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{row.email}</td>
                    <td className="px-3 py-2">{[row.telephone1, row.telephone2].filter(Boolean).join(", ")}</td>
                    <td className="px-3 py-2">{row.ebayUsername}</td>
                    <td className="px-3 py-2">{accountNumberFor(row.accountId) ? `#${accountNumberFor(row.accountId)}` : ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.created)}</td>
                    <td className="px-3 py-2">
                      {contactHistory(row)}
                      {row.leads > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleLeadsOpen(row.id)}
                          className="ml-2 font-semibold text-primary-600 hover:text-primary-700"
                        >
                          {leadsOpen.includes(row.id) ? t("Hide leads") : t("Show leads")}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">{status(row, keptContactId, onLeftOutAccount(row, keptAccountId, leftOut))}</td>
                  </tr>
                  {leadsOpen.includes(row.id) && (
                    <tr>
                      <td colSpan={10} className="bg-gray-50 px-3 py-2">
                        <ContactRowLeads contactId={row.id} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {group.accounts.length > 0 && (
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-700">{t("Accounts")}</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">{t("Keep")}</th>
                    <th className="px-3 py-2">{t("Merge")}</th>
                    <th className="px-3 py-2">{t("Account No")}</th>
                    <th className="px-3 py-2">{t("Name")}</th>
                    <th className="px-3 py-2">{t("Type")}</th>
                    <th className="px-3 py-2">{t("Bank details")}</th>
                    <th className="px-3 py-2">{t("Created")}</th>
                    <th className="px-3 py-2">{t("History")}</th>
                    <th className="px-3 py-2">{t("What happens")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.accounts.map((row) => (
                    <tr key={row.id} className={row.id === keptAccountId ? "bg-green-50" : ""}>
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          name={`kept-account-${group.keptContactId}`}
                          checked={row.id === keptAccountId}
                          onChange={() => keepAccount(row.id)}
                          aria-label={t("Keep this account")}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {row.id !== keptAccountId && (
                          <input
                            type="checkbox"
                            checked={!leftOut.includes(row.id)}
                            onChange={() => toggleLeftOut(row.id)}
                            aria-label={t("Merge this account")}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/accounts/${row.id}`} className="text-primary-600 hover:underline">
                          #{row.accountNumber}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{row.name}</td>
                      <td className="px-3 py-2">{typeLabel(row.accountType)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.bankDetails}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.created)}</td>
                      <td className="px-3 py-2">{accountHistory(row)}</td>
                      <td className="px-3 py-2">{status(row, keptAccountId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {choices.length > 1 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
            <label className="block font-medium text-amber-800" htmlFor={`bank-from-${group.keptContactId}`}>
              {t("These accounts have different bank details. Which should the kept account have?")}
            </label>
            <select
              id={`bank-from-${group.keptContactId}`}
              className="mt-2 block w-full rounded-md border-gray-300 text-sm"
              value={bankFrom?.accountId ?? ""}
              onChange={(e) => setBankFromId(e.target.value)}
            >
              {choices.map((c) => (
                <option key={c.accountId} value={c.accountId}>
                  {`#${c.accountNumber}: ${c.bankDetails}`}
                </option>
              ))}
            </select>
            <p className="mt-2 text-amber-800">
              {t("New leads for this person are paid to the details kept. The others are written into the account's notes, and old invoices keep the details they were paid to.")}
            </p>
          </div>
        )}

        {(sourceAccounts.length > 0 || sourceContacts.length > 0 || leftOutRows > 0) && (
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <p className="text-sm text-gray-600">
              {t("Merging moves everything onto the kept contact and account, history included. Choose which rows are kept with the buttons on the left, and untick any row that is someone else.")}
            </p>
            <ButtonInput
              label={t("Merge into kept")}
              isSubmit={false}
              onClick={() => setConfirmOpen(true)}
              classes="shrink-0"
              isLoading={isMerging}
              disabled={sourceAccounts.length === 0 && sourceContacts.length === 0}
            />
          </div>
        )}
      </Card>
    </>
  );
}
